import React, { useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";

import GeneralLayout from "components/PageLayouts/GeneralLayout";
import GenericRecordSelection from "components/Records/RecordSelection/GenericRecordSelection";

import { Event } from "api/Interactions/InteractionsInterfaces";
import { eventsAPI } from "api/Interactions/EventsAPI";

import AddEventPopup from "./Events/AddEventsPopup";
import EventsDetail from "./Events/EventsDetail";
import EventsRightDrawer from "./Events/EventsRightDrawer";
import EventsBottomDrawer from "./Events/EventsBottomDrawer";
import EventEmptyPage from "./Events/EventsEmptyPage";

const Events: React.FC = () => {
  const [eventsData, setEventsData] = useState<Event[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);

  const { id: routeId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Safely calculate active id (null instead of 0)
  const activeId =
    routeId ? Number(routeId) :
    searchParams.get("id") ? Number(searchParams.get("id")) :
    null;

  // Fetch list
  useEffect(() => {
    eventsAPI.getAll().then((data) => {
      setEventsData(data);
      setLoading(false);
    });
  }, []);

  // Fetch selected event from URL
  useEffect(() => {
    if (!activeId) {
      setSelectedEvent(null);
      return;
    }

    eventsAPI.getById(activeId)
      .then((data) => setSelectedEvent(data))
      .catch(() => setSelectedEvent(null));
  }, [activeId]);

  // Sidebar selection
  const handleSelectEvent = (eventId: number) => {
    navigate(`/interactions/events/${eventId}/`);
  };

  if (loading) return <CircularProgress />;
  
  return (
    <GeneralLayout
      useLeftDrawer
      useRightDrawer
      useBottomDrawer
      defaultOpenLeft={true}
      defaultOpenRight={false}
      defaultOpenBottom={false}
      leftDrawerContent={
        <GenericRecordSelection<Event>
          items={eventsData}
          selectedId={activeId ?? undefined}
          onSelect={(e) => handleSelectEvent(e.id)}
          AddPopupComponent={AddEventPopup}
          onCreated={(newEvent) => {
            setEventsData((prev) => [...prev, newEvent]);
            navigate(`/interactions/events/${newEvent.id}/`);
          }}
        />
      }
      rightDrawerContent={activeId ? <EventsRightDrawer eventId={activeId} /> : null}
      bottomDrawerContent={activeId ? <EventsBottomDrawer eventId={activeId} /> : null}
    >
      {selectedEvent ? (
        <EventsDetail
          eventId={activeId!}
          onClose={() => navigate("/interactions/events/")}
        />
      ) : (
        <EventEmptyPage
          onEventsCreated={(newEvent) => navigate(`/interactions/events/${newEvent.id}/`)}
        />
      )}
    </GeneralLayout>
  );
};

export default Events;
